import {
	RegulatoryAnswerCategoryKey,
	RegulatoryAssistantConfidence,
	RegulatoryAssistantMode,
	type PlanningPolicySource,
	type RegulatoryAnswerCategory,
	type RegulatoryAnswerItem,
	type RegulatoryAssistantResponse,
	type RegulatoryReference,
} from "@/types/regulatoryAssistant"

type RawRecord = Record<string, unknown>

function asRecord(value: unknown): RawRecord {
	return value && typeof value === "object" && !Array.isArray(value) ? (value as RawRecord) : {}
}

function asList(value: unknown): unknown[] {
	return Array.isArray(value) ? value : []
}

function asText(value: unknown): string {
	return typeof value === "string" || typeof value === "number" ? String(value).trim() : ""
}

function asNullableText(value: unknown): string | null {
	return asText(value) || null
}

function asTextList(value: unknown): string[] {
	return asList(value).map(asText).filter(Boolean)
}

function asEnumValue<T extends string>(values: Record<string, T>, value: unknown, fallback: T): T {
	return Object.values(values).find(candidate => candidate === value) ?? fallback
}

function mapItem(raw: unknown): RegulatoryAnswerItem {
	const item = asRecord(raw)
	return {
		title: asText(item.title),
		detail: asText(item.detail),
		meta: asTextList(item.meta),
		links: asList(item.links)
			.map(asRecord)
			.map(link => ({ label: asText(link.label), href: asText(link.href) }))
			.filter(link => link.label && link.href),
	}
}

function mapCategories(raw: unknown): RegulatoryAnswerCategory[] {
	return asList(raw)
		.map(asRecord)
		.flatMap(category => {
			const key = Object.values(RegulatoryAnswerCategoryKey).find(candidate => candidate === category.key)
			const items = asList(category.items).map(mapItem)
			return key && items.length > 0 ? [{ key, title: asText(category.title), items }] : []
		})
}

function mapPlanningPolicySource(raw: unknown): PlanningPolicySource {
	const policy = asRecord(raw)
	const minUnitsPerHectare = Number(policy.min_units_ha)
	return {
		name: asText(policy.name),
		type: asNullableText(policy.type) ?? asNullableText(policy.planif_type),
		version: asNullableText(policy.version),
		minUnitsPerHectare: Number.isFinite(minUnitsPerHectare) && minUnitsPerHectare > 0 ? minUnitsPerHectare : null,
		mapUrl: asNullableText(policy.url_map),
		documentUrl: asNullableText(policy.url_doc),
	}
}

function mapReference(raw: unknown): RegulatoryReference {
	const reference = asRecord(raw)
	const id = asText(reference.chunk_id)
	return {
		id,
		label: asText(reference.citation_label) || asText(reference.document_name) || id,
		documentType: asNullableText(reference.document_type),
		coverage: asNullableText(reference.coverage),
		articleRef: asNullableText(reference.article_ref),
		url: asNullableText(reference.source_fetch_url) ?? asNullableText(reference.source_metadata_url),
	}
}

export function mapRegulatoryAssistantResponse(raw: unknown): RegulatoryAssistantResponse {
	const payload = asRecord(raw)
	const answer = asRecord(payload.answer)
	const contextCard = payload.context_card ? asRecord(payload.context_card) : null

	return {
		mode: asEnumValue(RegulatoryAssistantMode, payload.mode, RegulatoryAssistantMode.General),
		confidence: asEnumValue(
			RegulatoryAssistantConfidence,
			payload.confidence ?? answer.confidence,
			RegulatoryAssistantConfidence.Low
		),
		answer: {
			title: asText(answer.title),
			summary: asText(answer.summary),
			categories: mapCategories(answer.categories),
			keyPoints: asTextList(answer.key_points),
			parcelContext: asTextList(answer.parcel_context),
			zoneContext: asTextList(answer.zone_context),
			watchouts: asTextList(answer.watchouts),
		},
		contextCard: contextCard && {
			label: asText(contextCard.label),
			parcelId: asNullableText(contextCard.parcel_id),
			zoneCodes: [...new Set([...asTextList([contextCard.zone_code]), ...asTextList(contextCard.zone_codes)])],
			activeFlags: asTextList(contextCard.active_flags),
			planningPolicySources: asList(contextCard.planning_policy_sources)
				.map(mapPlanningPolicySource)
				.filter(policy => policy.name),
		},
		references: asList(payload.references)
			.map(mapReference)
			.filter(reference => reference.id),
	}
}
